// The status map of the dashboard and the back-office, and the site location picker. MapLibre GL (about 250 kB
// gzip with its worker) loads only when a map is shown: StatusMap and LocationPicker are lazy components; the rest
// of this module (statuses, markers, legend, list) is light and loads with the page.

import { cx, Spinner } from '@cck/ui';
import { Component, lazy, type ReactNode, Suspense } from 'react';
import { MarkerIcon, type PointKind } from './marker.tsx';
import { type AssetStatusCode, STATUS_ORDER, type StatusCounts } from './status.ts';

export * from './status.ts';
export { MarkerIcon, type PointKind, STATUS_FILL } from './marker.tsx';
export { DEFAULT_VIEW, styleUrl, TILE_HOST } from './style.ts';

/** A place on the map: a site, or a vehicle where it last was. */
export interface MapPoint {
  id: string;
  kind: PointKind;
  lat: number;
  lon: number;
  status: AssetStatusCode;
  label: string;
  /** Who or where it belongs to (the client, the site), after the label. */
  detail?: string;
}

/** The words of a map: its controls, statuses and kinds of points. */
export interface MapLabels {
  region: string;
  zoomIn: string;
  zoomOut: string;
  attribution: string;
  unavailable: string;
  loading: string;
  status: Record<AssetStatusCode, string>;
  site: string;
  vehicle: string;
}

export interface StatusMapProps {
  points: MapPoint[];
  selected: string | null;
  onSelect: (id: string | null) => void;
  labels: MapLabels;
  /** Its size, e.g. "h-[60vh] w-full". */
  className?: string;
}

export interface LocationPickerProps {
  value: { lat: number; lon: number } | null;
  onChange: (v: { lat: number; lon: number }) => void;
  /** Where to start without a value ([longitude, latitude]): the organisation's other sites, say. */
  center?: [number, number] | undefined;
  labels: Omit<MapLabels, 'status' | 'site' | 'vehicle'> & { pin: string };
  className?: string;
}

const StatusMapView = lazy(() => import('./StatusMapView.tsx'));
const LocationPickerView = lazy(() => import('./LocationPickerView.tsx'));

/** If the map's code cannot load (offline, a new release replaced it), the page stays usable. */
class MapBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

function Placeholder({ className, children }: { className?: string | undefined; children: ReactNode }) {
  return <div className={cx('flex items-center justify-center bg-slate-100', className)}>{children}</div>;
}

export function StatusMap(props: StatusMapProps) {
  const fallback = <Placeholder className={props.className}><p className="p-6 text-sm text-slate-500">{props.labels.unavailable}</p></Placeholder>;
  return (
    <MapBoundary fallback={fallback}>
      <Suspense fallback={<Placeholder className={props.className}><Spinner label={props.labels.loading} /></Placeholder>}>
        <StatusMapView {...props} />
      </Suspense>
    </MapBoundary>
  );
}

export function LocationPicker(props: LocationPickerProps) {
  const fallback = <Placeholder className={props.className}><p className="p-4 text-sm text-slate-500">{props.labels.unavailable}</p></Placeholder>;
  return (
    <MapBoundary fallback={fallback}>
      <Suspense fallback={<Placeholder className={props.className}><Spinner label={props.labels.loading} /></Placeholder>}>
        <LocationPickerView {...props} />
      </Suspense>
    </MapBoundary>
  );
}

/** What the markers mean: the statuses (with counts, if given), and sites against vehicles. */
export function MapLegend({ labels, counts, className }: { labels: Pick<MapLabels, 'status' | 'site' | 'vehicle'> & { title: string };
  counts?: StatusCounts | undefined; className?: string }) {
  return (
    <div className={cx('flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600', className)}>
      <span className="font-medium text-slate-700">{labels.title}</span>
      <ul className="contents">
        {STATUS_ORDER.map((s) => (
          <li key={s} className="inline-flex items-center gap-1.5">
            <MarkerIcon kind="site" status={s} small />
            {labels.status[s]}{counts && <span className="tabular-nums text-slate-500">({counts[s]})</span>}
          </li>
        ))}
        <li className="inline-flex items-center gap-1.5"><MarkerIcon kind="site" status="no_data" small bare />{labels.site}</li>
        <li className="inline-flex items-center gap-1.5"><MarkerIcon kind="vehicle" status="no_data" small bare />{labels.vehicle}</li>
      </ul>
    </div>
  );
}

/** The points as a list of buttons: the map's equivalent for the keyboard and screen readers, and a quick index. */
export function MapPointList({ points, selected, onSelect, labels, className }: {
  points: MapPoint[]; selected: string | null; onSelect: (id: string) => void;
  labels: Pick<MapLabels, 'status' | 'site' | 'vehicle'>; className?: string;
}) {
  return (
    <ul className={cx('divide-y divide-slate-100', className)}>
      {points.map((p) => (
        <li key={p.id}>
          <button type="button" onClick={() => onSelect(p.id)} aria-pressed={p.id === selected}
            className={cx('flex w-full items-center gap-2.5 px-2 py-2 text-left text-sm hover:bg-slate-50',
              p.id === selected && 'bg-brand-50')}>
            <MarkerIcon kind={p.kind} status={p.status} small />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-slate-900">{p.label}</span>
              {p.detail && <span className="block truncate text-xs text-slate-500">{p.detail}</span>}
            </span>
            <span className="shrink-0 text-xs text-slate-500">
              <span className="sr-only">{p.kind === 'vehicle' ? labels.vehicle : labels.site}, </span>{labels.status[p.status]}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
